import { queryOptions } from '@tanstack/react-query';
import { LngLatBounds } from 'mapbox-gl';

import { resultsApi } from '../apis/results';
import {
  serializeResultsMapQueryKey,
  serializeResultsQueryFilters,
} from '../../lib/results-filters';

import {
  Filters,
  getQueriesWithGlobal,
  Globals,
  useQueriesWithGlobal,
} from '@data-access';

const RESULTS_LIST_STALE_TIME = 30_000;

export const resultsQueries = (global: Globals) => ({
  all: () => [global, 'results'],

  lists: () => [...resultsQueries(global).all(), 'lists'],
  list: (filters: Filters) =>
    queryOptions({
      queryKey: [
        ...resultsQueries(global).lists(),
        serializeResultsQueryFilters(filters),
      ],
      queryFn: () => resultsApi.list(filters),
      staleTime: RESULTS_LIST_STALE_TIME,
      refetchOnWindowFocus: false,
    }),

  summaries: () => [...resultsQueries(global).all(), 'summary'],
  summary: (filters: Filters) =>
    queryOptions({
      queryKey: [
        ...resultsQueries(global).summaries(),
        serializeResultsQueryFilters(filters),
      ],
      queryFn: () => resultsApi.getSummary(filters),
      staleTime: RESULTS_LIST_STALE_TIME,
      refetchOnWindowFocus: false,
    }),

  values: () =>
    queryOptions({
      queryKey: [...resultsQueries(global).all(), 'values'],
      queryFn: () => resultsApi.values(),
      staleTime: 5 * 60 * 1000,
    }),

  details: () => [...resultsQueries(global).all(), 'detail'],
  detail: (id: number) =>
    queryOptions({
      queryKey: [...resultsQueries(global).details(), id],
      queryFn: () => resultsApi.getById(id),
    }),

  mapList: (params: {
    bounds: LngLatBounds;
    cluster: boolean;
    zoom: number;
    tableFilters?: Filters;
  }) =>
    queryOptions({
      queryKey: [
        ...resultsQueries(global).all(),
        'map',
        serializeResultsMapQueryKey(params),
      ],
      queryFn: () =>
        resultsApi.getMapInBounds(
          params.bounds,
          params.cluster,
          params.zoom,
          params.tableFilters,
        ),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    }),
});

export const useResultsQueries = () => {
  const queries = useQueriesWithGlobal(resultsQueries);
  return queries;
};

export const getResultsQueries = () => getQueriesWithGlobal(resultsQueries);
